import { useState, type Ref } from 'react';
import { Button, Card, Group, Loader, SimpleGrid, Stack, Table, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useProjectAnalyses } from '@/api/analyses';
import { useProjectCost, useRefreshProjectCost, type ProjectCost } from '@/api/cost';
import { errorMessage } from '@/api/errors';
import { useProjectObjects, type DataObject } from '@/api/objects';
import { useFormat } from '@/components/useFormat';
import { DeleteObjectModal } from '@/features/objects/DeleteObjectModal';
import { brand } from '@/theme/theme';

const TOP = 5;
const linkStyle = { fontWeight: 600, color: brand.text, textDecoration: 'none' } as const;

/** One key figure: a small muted label over the bold value. */
export function CostFigure({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Stack gap={2}>
      <Text fz={12} c={brand.textMuted}>
        {label}
      </Text>
      <Text fz={20} fw={700}>
        {value}
      </Text>
      {sub && (
        <Text fz={12} c={brand.textMuted}>
          {sub}
        </Text>
      )}
    </Stack>
  );
}

/** The project's storage (N19, N20): totals, the largest objects and runs, originals that can go. */
export function ProjectCostCard({ projectId, cardRef }: { projectId: string; cardRef?: Ref<HTMLDivElement> }) {
  const { t } = useTranslation('ops');
  const { bytes, dateTime } = useFormat();
  const cost = useProjectCost(projectId);
  const refresh = useRefreshProjectCost(projectId);
  const doRefresh = async () => {
    try {
      await refresh.mutateAsync();
    } catch (err) {
      notifications.show({ color: 'red', message: errorMessage(err) });
    }
  };

  return (
    <Card data-testid="project-cost" ref={cardRef}>
      <Group justify="space-between" mb="sm" align="flex-start">
        <Stack gap={2}>
          <Title order={3}>{t('cost.title')}</Title>
          {cost.data && (
            <Text fz={12} c={brand.textMuted}>
              {cost.data.taken_at ? t('cost.takenAt', { when: dateTime(cost.data.taken_at) }) : t('cost.never')}
            </Text>
          )}
        </Stack>
        <Button size="xs" variant="default" loading={refresh.isPending} onClick={() => void doRefresh()}>
          {t('cost.refresh')}
        </Button>
      </Group>
      {cost.isPending ? (
        <Loader size="sm" color="forest" />
      ) : cost.isError ? (
        <Text fz={14} c="red">
          {errorMessage(cost.error)}
        </Text>
      ) : cost.data.taken_at ? (
        <CostBody projectId={projectId} cost={cost.data} bytes={bytes} />
      ) : null}
    </Card>
  );
}

function CostBody({ projectId, cost, bytes }: { projectId: string; cost: ProjectCost; bytes: (n: number) => string }) {
  const { t } = useTranslation('ops');
  const { t: tp } = useTranslation('projects');
  const { i18n } = useTranslation();
  const lang = i18n.language as 'cs' | 'en';
  const qc = useQueryClient();
  const analyses = useProjectAnalyses(projectId);
  const objects = useProjectObjects(projectId);
  const [toDropRaw, setToDropRaw] = useState<DataObject | null>(null);

  const analysisName = (id: string | null | undefined) => {
    const a = id ? analyses.data?.find((x) => x.public_id === id) : undefined;
    return a ? a.name[lang] || a.name.cs : id;
  };
  const processed = cost.objects.reduce((s, o) => s + o.processed, 0);
  const droppable = cost.objects.filter((o) => o.can_drop_raw);
  const reclaimable = droppable.reduce((s, o) => s + o.raw, 0);
  const topObjects = [...cost.objects]
    .filter((o) => o.raw + o.processed > 0)
    .sort((a, b) => b.raw + b.processed - (a.raw + a.processed))
    .slice(0, TOP);
  const topRuns = cost.runs.slice(0, TOP);

  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
        <CostFigure label={t('cost.total')} value={bytes(cost.total)} />
        <CostFigure label={t('cost.raw')} value={bytes(cost.raw)} />
        <CostFigure label={t('cost.pipeline')} value={bytes(cost.pipeline)} />
        <CostFigure label={t('cost.processed')} value={bytes(processed)} sub={t('cost.processedHint')} />
      </SimpleGrid>
      {reclaimable > 0 && (
        <Text fz={13} data-testid="cost-reclaimable">
          {t('cost.reclaimable', { bytes: bytes(reclaimable), count: droppable.length })}
        </Text>
      )}
      {cost.missing.length > 0 && (
        <Text fz={13} c="red">
          {t('cost.missing', { count: cost.missing.length })}
        </Text>
      )}
      <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
        <Stack gap={4}>
          <Text fz={13} fw={600}>
            {t('cost.topObjects')}
          </Text>
          {topObjects.length === 0 ? (
            <Text fz={13} c={brand.textMuted}>
              —
            </Text>
          ) : (
            <Table fz={13}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{tp('objects.name')}</Table.Th>
                  <Table.Th ta="right">{t('cost.raw')}</Table.Th>
                  <Table.Th ta="right">{t('cost.processed')}</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {topObjects.map((o) => {
                  const full = objects.data?.find((x) => x.public_id === o.object);
                  return (
                    <Table.Tr key={o.object}>
                      <Table.Td>
                        <Link to="/staff/objects/$objectId" params={{ objectId: o.object }} style={linkStyle}>
                          {o.name || o.declared_name || o.object}
                        </Link>
                      </Table.Td>
                      <Table.Td ta="right">{bytes(o.raw)}</Table.Td>
                      <Table.Td ta="right">{bytes(o.processed)}</Table.Td>
                      <Table.Td ta="right">
                        {o.can_drop_raw && full && (
                          <Button size="compact-xs" variant="subtle" color="red" onClick={() => setToDropRaw(full)}>
                            {t('cost.dropRaw')}
                          </Button>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
        <Stack gap={4}>
          <Text fz={13} fw={600}>
            {t('cost.topRuns')}
          </Text>
          {topRuns.length === 0 ? (
            <Text fz={13} c={brand.textMuted}>
              —
            </Text>
          ) : (
            <Table fz={13}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('cost.run')}</Table.Th>
                  <Table.Th>{t('cost.analysis')}</Table.Th>
                  <Table.Th ta="right">{t('cost.size')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {topRuns.map((r) => (
                  <Table.Tr key={r.run}>
                    <Table.Td>
                      <Link to="/staff/runs/$runId" params={{ runId: r.run }} style={linkStyle}>
                        {r.type ?? r.run}
                      </Link>
                    </Table.Td>
                    <Table.Td>
                      {r.analysis ? (
                        <Link to="/staff/analyses/$analysisId" params={{ analysisId: r.analysis }} style={{ color: brand.text }}>
                          {analysisName(r.analysis)}
                        </Link>
                      ) : (
                        <Text span fz={13} c={brand.textMuted}>
                          {t('cost.intake')}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td ta="right">{bytes(r.bytes)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      </SimpleGrid>
      <DeleteObjectModal
        object={toDropRaw}
        mode="raw"
        onClose={() => {
          setToDropRaw(null);
          void qc.invalidateQueries({ queryKey: ['cost'] });
        }}
      />
    </Stack>
  );
}
